import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/services/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Truck,
  Smartphone,
  ChevronRight,
  ChevronLeft,
  CheckCircle,
  HelpCircle,
  Send,
  ShoppingCart,
  ArrowLeft,
} from "lucide-react";

const TIPOS_DOCUMENTO = [
  { value: "cc", label: "Cédula de Ciudadanía" },
  { value: "ce", label: "Cédula de Extranjería" },
  { value: "pasaporte", label: "Pasaporte" },
  { value: "nit", label: "NIT" },
];

const FAQ_ITEMS = [
  {
    question: "¿Cuánto puedo ganar?",
    answer: "Las ganancias dependen de tus ventas. Recibes comisión por cada producto vendido más bonos por metas alcanzadas. Vendedores activos ganan entre $800.000 y $2.000.000 mensuales."
  },
  {
    question: "¿Necesito experiencia previa?",
    answer: "No es necesaria experiencia previa. Te brindamos capacitación completa sobre los productos y técnicas de venta."
  },
  {
    question: "¿Cuál es la diferencia entre Digital y Carrito?",
    answer: "Digital: Vendes desde tu celular, tomando pedidos que se entregan desde central. Carrito: Vendes directamente en zona con un carrito de productos."
  },
  {
    question: "¿Cuándo recibo mis pagos?",
    answer: "Comisiones base semanales (cada lunes). Bonos extra mensuales (primer día del mes)."
  },
  {
    question: "¿Qué pasa después de registrarme?",
    answer: "Revisamos tu solicitud en 24-48 horas. Si es aprobada, recibirás un correo con tus credenciales de acceso."
  },
];

export default function RegistroVendedor() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const [form, setForm] = useState({
    nombre: "",
    email: "",
    telefono: "",
    tipo_documento: "cc",
    documento_id: "",
    direccion: "",
    tipo_vendedor: "",
    requiere_carro: false,
    notas: "",
  });

  const [errors, setErrors] = useState({});

  const validateStep = (currentStep) => {
    const newErrors = {};
    
    if (currentStep === 1) {
      if (!form.nombre.trim()) newErrors.nombre = "Nombre requerido";
      if (!form.email.trim()) newErrors.email = "Email requerido";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) newErrors.email = "Email inválido";
      if (!form.telefono.trim()) newErrors.telefono = "Teléfono requerido";
    }
    
    if (currentStep === 2) {
      if (!form.tipo_documento) newErrors.tipo_documento = "Selecciona tipo de documento";
      if (!form.documento_id.trim()) newErrors.documento_id = "Documento requerido";
      if (!form.direccion.trim()) newErrors.direccion = "Dirección requerida";
    }
    
    if (currentStep === 3) {
      if (!form.tipo_vendedor) newErrors.tipo_vendedor = "Selecciona un tipo de vendedor";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep(3)) return;
    
    setSubmitting(true);
    try {
      await api.post("/solicitudes/registro", {
        nombre: form.nombre.trim(),
        email: form.email.toLowerCase().trim(),
        telefono: form.telefono.trim(),
        direccion: form.direccion.trim(),
        tipo_documento: form.tipo_documento,
        documento_id: form.documento_id.trim(),
        tipo_vendedor: form.tipo_vendedor,
        requiere_carro: form.requiere_carro,
        notas: form.notas.trim() || null,
      });
      setSubmitted(true);
      toast.success("¡Solicitud enviada exitosamente!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar solicitud");
    } finally {
      setSubmitting(false);
    }
  };

  // Success screen after submission
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-torta-cream to-white flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="font-display text-2xl text-torta-dark mb-3">¡Solicitud Enviada!</h1>
          <p className="text-torta-muted mb-6">
            Hemos recibido tu solicitud de registro. Nuestro equipo la revisará en las próximas 24-48 horas.
          </p>
          <div className="bg-torta-cream rounded-xl p-4 mb-6">
            <p className="text-sm text-torta-dark">
              <strong>¿Qué sigue?</strong><br />
              Si tu solicitud es aprobada, recibirás un correo a <strong>{form.email}</strong> con tus credenciales de acceso.
            </p>
          </div>
          <Button
            onClick={() => navigate("/")}
            className="w-full bg-torta-brown hover:bg-torta-brown/90"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Volver al Inicio
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-torta-cream to-white">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="Logo" className="w-12 h-12 rounded-full" onError={(e) => e.target.style.display = 'none'} />
            <div>
              <h1 className="font-display text-xl text-torta-dark">Torta de Cuajada</h1>
              <p className="text-xs text-torta-muted">Únete a nuestro equipo</p>
            </div>
          </Link>
          <Link to="/" className="text-torta-brown hover:text-torta-brown/80 text-sm flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Volver
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  {[1, 2, 3].map((s) => (
                    <div key={s} className="flex items-center">
                      <div
                        className={`w-10 h-10 rounded-full flex items-center justify-center font-medium transition-all ${
                          step >= s
                            ? "bg-torta-brown text-white"
                            : "bg-gray-200 text-gray-500"
                        }`}
                      >
                        {step > s ? <CheckCircle className="w-5 h-5" /> : s}
                      </div>
                      {s < 3 && (
                        <div
                          className={`w-16 sm:w-24 h-1 mx-2 rounded ${
                            step > s ? "bg-torta-brown" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-xs text-torta-muted px-1">
                  <span>Datos personales</span>
                  <span>Identificación</span>
                  <span>Tipo de venta</span>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {/* Step 1: Personal Data */}
                {step === 1 && (
                  <div className="space-y-5" data-testid="step-1">
                    <div className="text-center mb-6">
                      <h2 className="font-display text-2xl text-torta-dark">Datos Personales</h2>
                      <p className="text-torta-muted">Cuéntanos sobre ti</p>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <User className="w-4 h-4 text-torta-brown" />
                        Nombre completo *
                      </Label>
                      <Input
                        value={form.nombre}
                        onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                        placeholder="Tu nombre completo"
                        className={errors.nombre ? "border-red-500" : ""}
                      />
                      {errors.nombre && <p className="text-red-500 text-xs">{errors.nombre}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-torta-brown" />
                        Correo electrónico *
                      </Label>
                      <Input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        placeholder="tu@correo.com"
                        className={errors.email ? "border-red-500" : ""}
                      />
                      {errors.email && <p className="text-red-500 text-xs">{errors.email}</p>}
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-torta-brown" />
                        Teléfono / WhatsApp *
                      </Label>
                      <Input
                        value={form.telefono}
                        onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                        placeholder="300 123 4567"
                        className={errors.telefono ? "border-red-500" : ""}
                      />
                      {errors.telefono && <p className="text-red-500 text-xs">{errors.telefono}</p>}
                    </div>
                  </div>
                )}

                {/* Step 2: Identification */}
                {step === 2 && (
                  <div className="space-y-5" data-testid="step-2">
                    <div className="text-center mb-6">
                      <h2 className="font-display text-2xl text-torta-dark">Identificación</h2>
                      <p className="text-torta-muted">Información para verificar tu identidad</p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-torta-brown" />
                          Tipo de documento *
                        </Label>
                        <select
                          value={form.tipo_documento}
                          onChange={(e) => setForm({ ...form, tipo_documento: e.target.value })}
                          className={`w-full h-12 px-3 rounded-lg border bg-white focus:outline-none focus:ring-2 focus:ring-torta-brown/50 ${errors.tipo_documento ? "border-red-500" : "border-gray-200"}`}
                        >
                          {TIPOS_DOCUMENTO.map((tipo) => (
                            <option key={tipo.value} value={tipo.value}>
                              {tipo.label}
                            </option>
                          ))}
                        </select>
                        {errors.tipo_documento && <p className="text-red-500 text-xs">{errors.tipo_documento}</p>}
                      </div>

                      <div className="space-y-2">
                        <Label>Número de documento *</Label>
                        <Input
                          value={form.documento_id}
                          onChange={(e) => setForm({ ...form, documento_id: e.target.value })}
                          placeholder="1234567890"
                          className={errors.documento_id ? "border-red-500" : ""}
                        />
                        {errors.documento_id && <p className="text-red-500 text-xs">{errors.documento_id}</p>}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-torta-brown" />
                        Dirección de residencia *
                      </Label>
                      <Input
                        value={form.direccion}
                        onChange={(e) => setForm({ ...form, direccion: e.target.value })}
                        placeholder="Calle, número, barrio, ciudad"
                        className={errors.direccion ? "border-red-500" : ""}
                      />
                      {errors.direccion && <p className="text-red-500 text-xs">{errors.direccion}</p>}
                    </div>
                  </div>
                )}

                {/* Step 3: Vendor Type */}
                {step === 3 && (
                  <div className="space-y-5" data-testid="step-3">
                    <div className="text-center mb-6">
                      <h2 className="font-display text-2xl text-torta-dark">Tipo de Vendedor</h2>
                      <p className="text-torta-muted">¿Cómo te gustaría vender?</p>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tipo_vendedor: "digital", requiere_carro: false })}
                        className={`p-6 rounded-2xl border-2 text-left transition-all ${
                          form.tipo_vendedor === "digital"
                            ? "border-blue-500 bg-blue-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                          form.tipo_vendedor === "digital" ? "bg-blue-500 text-white" : "bg-gray-100 text-gray-600"
                        }`}>
                          <Smartphone className="w-6 h-6" />
                        </div>
                        <h3 className={`font-medium text-lg mb-1 ${form.tipo_vendedor === "digital" ? "text-blue-800" : "text-gray-800"}`}>
                          Vendedor Digital
                        </h3>
                        <p className={`text-sm ${form.tipo_vendedor === "digital" ? "text-blue-600" : "text-gray-500"}`}>
                          Toma pedidos desde tu celular. Los productos se entregan desde central.
                        </p>
                      </button>

                      <button
                        type="button"
                        onClick={() => setForm({ ...form, tipo_vendedor: "carrito" })}
                        className={`p-6 rounded-2xl border-2 text-left transition-all ${
                          form.tipo_vendedor === "carrito"
                            ? "border-amber-500 bg-amber-50"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-3 ${
                          form.tipo_vendedor === "carrito" ? "bg-amber-500 text-white" : "bg-gray-100 text-gray-600"
                        }`}>
                          <ShoppingCart className="w-6 h-6" />
                        </div>
                        <h3 className={`font-medium text-lg mb-1 ${form.tipo_vendedor === "carrito" ? "text-amber-800" : "text-gray-800"}`}>
                          Vendedor Carrito
                        </h3>
                        <p className={`text-sm ${form.tipo_vendedor === "carrito" ? "text-amber-600" : "text-gray-500"}`}>
                          Vende directamente en una zona asignada con productos físicos.
                        </p>
                      </button>
                    </div>
                    {errors.tipo_vendedor && <p className="text-red-500 text-xs text-center">{errors.tipo_vendedor}</p>}

                    {/* Cart requirement for Carrito type */}
                    {form.tipo_vendedor === "carrito" && (
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={form.requiere_carro}
                            onChange={(e) => setForm({ ...form, requiere_carro: e.target.checked })}
                            className="mt-1 w-5 h-5 rounded border-amber-300 text-amber-600 focus:ring-amber-500"
                          />
                          <div>
                            <span className="font-medium text-amber-800 flex items-center gap-2">
                              <Truck className="w-4 h-4" />
                              Necesito carro para venta diaria
                            </span>
                            <p className="text-sm text-amber-600 mt-1">
                              Marca esta opción si necesitas que te proporcionemos un carro para transportar productos en tu zona.
                            </p>
                          </div>
                        </label>
                      </div>
                    )}

                    {/* Notes */}
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-torta-brown" />
                        Notas adicionales (opcional)
                      </Label>
                      <Textarea
                        value={form.notas}
                        onChange={(e) => setForm({ ...form, notas: e.target.value })}
                        placeholder="¿Tienes experiencia en ventas? ¿Alguna preferencia de zona? Cuéntanos..."
                        rows={3}
                      />
                    </div>

                    {/* Summary */}
                    <div className="bg-torta-cream rounded-xl p-4">
                      <h4 className="font-medium text-torta-dark mb-2">Resumen de tu solicitud</h4>
                      <div className="text-sm text-torta-muted space-y-1">
                        <p><strong>Nombre:</strong> {form.nombre}</p>
                        <p><strong>Email:</strong> {form.email}</p>
                        <p><strong>Teléfono:</strong> {form.telefono}</p>
                        <p><strong>Documento:</strong> {TIPOS_DOCUMENTO.find(t => t.value === form.tipo_documento)?.label} {form.documento_id}</p>
                        <p><strong>Tipo:</strong> {form.tipo_vendedor === "digital" ? "Vendedor Digital" : "Vendedor Carrito"}</p>
                        {form.tipo_vendedor === "carrito" && (
                          <p><strong>Requiere carro:</strong> {form.requiere_carro ? "Sí" : "No"}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-gray-100">
                  {step > 1 ? (
                    <Button type="button" variant="outline" onClick={prevStep}>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Anterior
                    </Button>
                  ) : (
                    <div />
                  )}

                  {step < 3 ? (
                    <Button type="button" onClick={nextStep} className="bg-torta-brown hover:bg-torta-brown/90">
                      Siguiente
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  ) : (
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      {submitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Enviando...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Send className="w-4 h-4" />
                          Enviar Solicitud
                        </span>
                      )}
                    </Button>
                  )}
                </div>
              </form>
            </div>
          </div>

          {/* FAQ Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-8">
              <h3 className="font-display text-lg text-torta-dark mb-4 flex items-center gap-2">
                <HelpCircle className="w-5 h-5 text-torta-brown" />
                Preguntas Frecuentes
              </h3>
              <div className="space-y-3">
                {FAQ_ITEMS.map((faq, index) => (
                  <div key={index} className="border-b border-gray-100 pb-3">
                    <button
                      type="button"
                      onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                      className="w-full text-left font-medium text-sm text-torta-dark hover:text-torta-brown transition-colors flex items-start gap-2"
                    >
                      <span className={`transform transition-transform ${expandedFaq === index ? "rotate-90" : ""}`}>
                        <ChevronRight className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      </span>
                      {faq.question}
                    </button>
                    {expandedFaq === index && (
                      <p className="text-sm text-torta-muted mt-2 pl-6">
                        {faq.answer}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Contact info */}
              <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-sm text-torta-muted">
                  ¿Tienes más preguntas?<br />
                  <a href="https://api.whatsapp.com/send/?phone=573196536874&type=phone_number&app_absent=0" target="_blank" rel="noopener noreferrer" className="text-torta-brown hover:underline">
                    Escríbenos por WhatsApp
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
